import { useCallback, useContext } from 'react';
import { CustomerDesignResponse } from '@vp/upload-and-print-validator';
import { useClientValidation } from 'src/clientValidation';
import { abortControllerManager } from 'src/managers/AbortControllerManager';
import { UploadContext, UploadContextProps } from 'src/providers/UploadContextProvider';
import { processAndValidateUploadFromClient } from 'src/services/UploadAndPrintClientService';
import { processAndValidateUpload, Upload } from 'src/services/UploadAndPrintService';
import { filterValidationToDisplay } from 'src/utils/filterValidationToDisplay';
import { noticeError } from 'src/utils/newRelicUtil';
import { createTrackingDataForProduct } from 'src/utils/trackingUtil';
import { hasValidationErrors } from 'src/utils/validationUtil';
import { useErrorContext } from './ErrorContextHooks';
import { useProductContext } from './ProductContextHooks';
import { useStudioContext } from './StudioContextHooks';
import { useTrackingContext } from './TrackingContextHooks';
import { PricingContext, usePricingContext } from './usePricingContext';
import { useValidationContext } from './ValidationContextHooks';

export interface ProcessUpload {
  processUpload: (
    uploadedFile: Upload,
    shouldUseClientValidation: boolean
  ) => Promise<{ newUpload: Upload; processUploadResponse: CustomerDesignResponse } | null>;
  reprocessUpload: () => Promise<{ newUpload: Upload; processUploadResponse: CustomerDesignResponse } | null>;
}

export function useUploadContext(): UploadContextProps {
  return useContext(UploadContext);
}

export function useProcessUpload(): ProcessUpload {
  const { upload, setUpload } = useUploadContext();
  const { setValidations, setIsValidating, validationManager, fixedPageSizeWarning } = useValidationContext();
  const { studioUrl } = useStudioContext();
  const { setError } = useErrorContext();
  const { productInformation, setProductInformation } = useProductContext();
  const { trackingConfiguration } = useTrackingContext();
  const trackingDataForProduct = createTrackingDataForProduct(productInformation);
  const pricingContext = usePricingContext() || ({} as PricingContext);
  const { isClientValidationEnabled, setClientValidation } = useClientValidation();

  const updateValidationResults = useCallback(
    (uploadedFile: Upload, processUploadResponse: CustomerDesignResponse, processedUploadUri?: string) => {
      // Show previews only if there are no errors in validations or the escape hatch to studio is available
      const renderingInstructionsUrl =
        studioUrl || !hasValidationErrors(processUploadResponse.validations)
          ? processUploadResponse.renderingInstructionsUrl
          : undefined;

      const cimDocUrl = processUploadResponse.cimDocUrl;

      const newUpload = { ...uploadedFile, processedUploadUri, renderingInstructionsUrl, cimDocUrl };

      setUpload(newUpload);

      const hasNewProductRecommendation = Boolean(processUploadResponse.productData.price);
      /**
       * When there is a price update, we do not show the validation errors modal
       */
      setValidations(processUploadResponse.validations, !hasNewProductRecommendation);

      if (hasNewProductRecommendation) {
        setProductInformation({
          ...productInformation,
          recommendedProductData: processUploadResponse.productData.price && {
            price: processUploadResponse.productData.price,
            productOptions: processUploadResponse.productData.productOptions,
          },
        });
      }
      return { newUpload, processUploadResponse };
    },
    [productInformation, setProductInformation, setUpload, setValidations, studioUrl]
  );

  const processUpload = useCallback(
    async (
      uploadedFile: Upload,
      shouldUseClientValidation: boolean
    ): Promise<{ newUpload: Upload; processUploadResponse: CustomerDesignResponse } | null> => {
      if (!uploadedFile) {
        return null;
      }

      const abortSignal = abortControllerManager.getSignal();

      setIsValidating(true);
      try {
        if (!shouldUseClientValidation || !validationManager) {
          setValidations(null);
          const [processedUploadUri, processUploadResponse] = await processAndValidateUpload(
            uploadedFile.fileUri,
            productInformation,
            trackingConfiguration,
            trackingDataForProduct,
            pricingContext.vatInclusive,
            fixedPageSizeWarning,
            abortSignal
          );
          return updateValidationResults(uploadedFile, processUploadResponse, processedUploadUri);
        } else {
          const response = await processAndValidateUploadFromClient(
            validationManager,
            uploadedFile.fileUri,
            productInformation,
            trackingConfiguration,
            trackingDataForProduct,
            pricingContext.vatInclusive,
            fixedPageSizeWarning,
            abortSignal
          );

          if (!response) {
            return null;
          }
          const [processedUploadUri, processUploadResponse] = response;
          const filteredValidation = filterValidationToDisplay(processUploadResponse.validations);
          /**
           * We only need to show selected validations
           */
          processUploadResponse.validations = filteredValidation;
          return updateValidationResults(uploadedFile, processUploadResponse, processedUploadUri);
        }
      } catch (e) {
        if ((e as Error).name === 'AbortError') {
          // Silent catch as this is expected
          return null;
        }
        console.error('Error processing file', e);
        noticeError(e, {
          method: 'processUpload',
          uploadedFile: uploadedFile,
        });
        setError(e);
        return null;
      } finally {
        setIsValidating(false);
        setClientValidation(isClientValidationEnabled);
      }
    },
    [
      setIsValidating,
      validationManager,
      setValidations,
      productInformation,
      trackingConfiguration,
      trackingDataForProduct,
      pricingContext.vatInclusive,
      fixedPageSizeWarning,
      updateValidationResults,
      setError,
      setClientValidation,
      isClientValidationEnabled,
    ]
  );

  const reprocessUpload = useCallback(async () => {
    if (!upload) {
      return null;
    }

    const newUpload: Upload = {
      ...upload,
      processedUploadUri: '',
      renderingInstructionsUrl: '',
    };
    setUpload(newUpload);
    return await processUpload(newUpload, isClientValidationEnabled);
  }, [upload, setUpload, processUpload, isClientValidationEnabled]);

  return { processUpload, reprocessUpload };
}
